import React, { useRef, useState, useEffect } from "react";
import Draggable from "react-draggable";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CamerasGrid from "../../cartCameras/CamerasGrid";
import styles from "./DraggableCartPanel.module.css";
import OperatorTreatmentResponseEvent from "../../../services/operatorTreatmentResponseEvent";
import Swal from "sweetalert2";
import useAuth from "../../../hooks/Auth/useAuth";
import CartCamerasCarousel from "../../cartCameras/CartCamerasCarousel";
import common_types from "../../../common_files/common_types";

import useRTSPContext from "../../../redux/context/RTSPProvider";
import CartRtspViewer from "../../cartCameras/cartRtspViewer/CartRtspViewer";

const DraggableCartPanel = ({
  cartId,
  cartEventOps,
  onClose,
  eventType,
  eventSubtype,
  eventName,
  parameters = "",
  renderButtonsRows,
  inSlider = false,
}) => {
  const { relevantUrl, relevantSite, isPhoneSize } = useAuth();
  const { cameraMap } = useRTSPContext();
  
  const hasRtsp =
    cameraMap?.[relevantSite]?.[cartId] &&
    Object.keys(cameraMap[relevantSite][cartId]).length > 0;


  const dragRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(0);
// src/constants/operatorTexts.ts

const operatorOptionList = common_types.operation_cart_event_list;

const operatorTextMap = {
  [operatorOptionList.OPERATION_RESOLVED]:        "Resolved",
  [operatorOptionList.OPERATION_IGNORE_ISSUE]:    "Ignore Issue",
  [operatorOptionList.OPERATION_BYPASS]:          "Bypass",
  [operatorOptionList.OPERATION_IGNORE_OBSTACLE]: "Ignore Obstacle",
  [operatorOptionList.OPERATION_REVERSE]:         "Reverse",
  [operatorOptionList.OPERATION_MANUAL_PLANNING]: "Manual Planning",
  [operatorOptionList.OPERATION_DRIVE]:       "Drive",
  [operatorOptionList.OPERATION_PAUSE]:       "Pause",
};
  // Track panel width using ResizeObserver
  useEffect(() => {
    const el = dragRef.current;
    if (!el) return;
    // initial width
    setPanelWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver(entries => {
      for (let entry of entries) {
        setPanelWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);


  const handleSolveOperator = async (operator) => {
    try {
      const response = await OperatorTreatmentResponseEvent(
        cartId,
        eventType,
        eventSubtype,
        eventName,
        operator,
        "",
        relevantUrl
      );

      if (response === "ok") {
        Swal.fire({
          icon: "success",
          title: `Operation ${operator} succeeded!`,
          position: "center",
          confirmButtonColor: "#00A7EE",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: `Operation ${operator} failed: ${response}`,
          position: "center",
          confirmButtonColor: "#00A7EE",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: `An error occurred during ${operator}!`,
        text: error.message,
        position: "center",
        confirmButtonColor: "#00A7EE",
      });
    }
  };

  const PanelBody = (
      <div
        ref={dragRef}
        className={`${styles.draggableCartPanel} ${inSlider ? styles.inSlider : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ───── header / drag handle ───── */}
        <div
          className={`drag-handle ${styles.dragHandle}`}
          onClick={(e) => e.stopPropagation()}
          style={inSlider ? { cursor: 'default' } : undefined}
        >
          <span className={styles.panelTitle}>
            Cart {cartId} · {eventName}
          </span>
          {!inSlider && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="close"
              style={{ color: "#333" }}
              size="large">
              <CloseIcon />
            </IconButton>
          )}
        </div>

        {/* ───── operator actions under title (both desktop & phone) ───── */}
        <div className={styles.panelContent}
             style={{ paddingTop: inSlider ? 8 : undefined }}>
          <div className={styles.operations}>
            {cartEventOps.map((op) => (
              <button
                key={op}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSolveOperator(op);
                }}
                className={styles.operationButton}
              >
                {operatorTextMap[op] ?? op}
              </button>
            ))}
          </div>
        </div>

        {/* ───── content ───── */}
        <div className={styles.panelContent}>
          {/* cameras section/media */}
          {hasRtsp ? (
            <div className={styles.rtspContainer}>
              <CartRtspViewer
                site={relevantSite}
                cartId={cartId}
                isPhoneSize={isPhoneSize}
              />
            </div>
          ) : isPhoneSize || panelWidth < 750 ? (
            <div className={styles.cartCamerasCarouselContainer}>
              <CartCamerasCarousel cartId={cartId} />
            </div>
          ) : (
            <div className={styles.camerasGridContainer}>
              <CamerasGrid cartId={cartId} />
            </div>
          )}

          {/* optional extra rows: render BEFORE sticky operations on phone */}
      {renderButtonsRows && (
            <div className={styles.buttonsRowsContainer}>
        {renderButtonsRows(cartEventOps, handleSolveOperator)}
            </div>
          )}
        </div>

        {/* bottom invisible drag area (hidden in slider mode) */}
        {!inSlider && (
          <div
            className={`drag-handle ${styles.dragHandleBottom}`}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
  );

  return inSlider ? PanelBody : (
    <Draggable nodeRef={dragRef} handle=".drag-handle">
      {PanelBody}
    </Draggable>
  );
};

export default DraggableCartPanel;

export const AssistCartPanel = (props) => (
  <DraggableCartPanel {...props} inSlider />
);
